/**
 * Application-wide constants
 * Centralizes magic numbers and strings for maintainability
 */

// Animation timing constants
export const ANIMATION = {
  /** Default intersection observer threshold */
  INTERSECTION_THRESHOLD: 0.2,
  /** Stagger delay between animated items (ms) */
  STAGGER_DELAY_MS: 100,
  /** Row stagger delay for grid animations (ms) */
  ROW_STAGGER_DELAY_MS: 150,
  /** Column stagger delay for grid animations (ms) */
  COL_STAGGER_DELAY_MS: 100,
  /** Reveal animation duration (ms) */
  REVEAL_DURATION_MS: 700,
  /** Page transition duration (ms) */
  PAGE_TRANSITION_MS: 300,
  /** Reduced motion page transition (ms) */
  REDUCED_MOTION_TRANSITION_MS: 150,
} as const;

// Form field options
export const FORM_OPTIONS = {
  services: [
    { value: 'general-inspection', label: 'General Roof Inspection' },
    { value: 'leak-damage', label: 'Leak or Damage Repair' },
    { value: 'storm-damage', label: 'Storm Damage Assessment' },
    { value: 'roof-replacement', label: 'Full Roof Replacement' },
    { value: 'insurance-claim', label: 'Insurance Claim Help' },
    { value: 'preventative', label: 'Preventative Maintenance' },
    { value: 'emergency', label: 'Emergency Repair' },
  ],
  timelines: [
    { value: 'asap', label: 'As soon as possible' },
    { value: 'week', label: 'Within a week' },
    { value: 'month', label: 'Within a month' },
    { value: 'planning', label: 'Just planning ahead' },
  ],
  referrerTypes: [
    { value: 'realtor', label: 'Realtor / Real Estate Agent' },
    { value: 'home-inspector', label: 'Home Inspector' },
    { value: 'insurance-agent', label: 'Insurance Agent' },
    { value: 'canvasser', label: 'Canvasser' },
    { value: 'lead-generator', label: 'Lead Generator' },
    { value: 'past-customer', label: 'Past Customer' },
    { value: 'business-partner', label: 'Business Partner' },
    { value: 'other', label: 'Other' },
  ],
  referralServices: [
    { value: 'free-inspection', label: 'Free Roof Inspection' },
    { value: 'roof-repair', label: 'Roof Repair' },
    { value: 'roof-replacement', label: 'Roof Replacement' },
    { value: 'storm-damage', label: 'Emergency / Storm Damage' },
    { value: 'consultation', label: 'Not Sure / Need Consultation' },
  ],
  referralStatuses: [
    { value: 'pending', label: 'Pending' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'quoted', label: 'Quoted' },
    { value: 'closed_won', label: 'Completed' },
    { value: 'closed_lost', label: 'Lost' },
    { value: 'paid', label: 'Bonus Paid' },
  ],
} as const;

// File upload constants
export const UPLOAD = {
  /** Maximum file size in bytes (10MB) */
  MAX_FILE_SIZE_BYTES: 10 * 1024 * 1024,
  /** Storage bucket name */
  BUCKET_NAME: 'blog-images',
  /** Cache control header value */
  CACHE_CONTROL: '3600',
} as const;

// Query cache times
export const CACHE = {
  /** Stale time for neighborhood suggestions (ms) */
  NEIGHBORHOOD_SUGGESTIONS_STALE_MS: 30000,
} as const;

// Image sizing
export const IMAGE_SIZES = {
  /** Responsive image breakpoints */
  BREAKPOINTS: {
    small: 400,
    medium: 800,
    large: 1200,
  },
  /** Default sizes attribute for responsive images */
  DEFAULT_SIZES: '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw',
} as const;

// Phone number
export const CONTACT = {
  PHONE: '(346) 220-0989',
  PHONE_HREF: 'tel:+13462200989',
} as const;
